"use client";

import { motion, useInView } from "framer-motion";
import { Quote, Trophy } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { TypingText } from "@/components/ui/typing-text";
import { IMAGES } from "@/lib/constants/images";

export function JamesSpragueSection() {
	const ref = useRef<HTMLElement>(null);
	const isInView = useInView(ref, { once: true, margin: "-100px" });

	return (
		<section
			ref={ref}
			className="relative py-20 md:py-32 bg-white dark:bg-black overflow-hidden"
		>
			<div className="container mx-auto px-4">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						animate={isInView ? { opacity: 1, x: 0 } : {}}
						transition={{ duration: 0.6 }}
						className="space-y-8 z-10"
					>
						<div className="space-y-4">
							<span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-bold tracking-wide uppercase">
								<Trophy className="w-4 h-4" />
								2024 CrossFit Games Champion
							</span>
							<h2 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 dark:text-white leading-tight">
								FUEL LIKE AN <span className="text-primary">ATHLETE</span>
							</h2>

							<p className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed">
								2024 CrossFit Games Champion James Sprague doesn't leave his
								meals to chance. When you're training to compete with the best,
								every bite matters.
							</p>

							<p className="text-lg font-medium text-zinc-900 dark:text-white">
								That's why he trusts Ice Age Meals
							</p>
						</div>

						<div className="space-y-4">
							<p className="text-zinc-600 dark:text-zinc-400">
								Our high-protein meals take the guesswork out of nutrition.
								They’re clean, craveable, and ready when life gets
								hectic—whether you're mid-training, mid-shift, or just trying to
								stay on track.
							</p>
							<p className="text-zinc-600 dark:text-zinc-400 italic">
								You don’t have to be an athlete to eat like one.
							</p>
						</div>

						<div className="bg-zinc-900 dark:bg-white p-6 rounded-2xl shadow-sm border border-zinc-800 dark:border-zinc-200 relative">
							<Quote className="absolute top-4 right-4 w-8 h-8 text-white/10 dark:text-zinc-900/10" />
							<h3 className="text-xl font-bold mb-3 uppercase tracking-wide text-white dark:text-zinc-900">
								Built To Perform
							</h3>
							<p className="text-sm md:text-base text-zinc-300 dark:text-zinc-600 italic mb-4 min-h-[140px] sm:min-h-[120px]">
								<TypingText
									text={
										"\"Ice Age Meals are the real deal. As an athlete, it's super important that I put clean ingredients in my body... It allows me to be the best version of myself - as an athlete, as a husband, as a business man. I've used Ice Age Meals for over 5 years. Take my word for it.\""
									}
									speed={0.01}
									showCursor={false}
								/>
							</p>
							<p className="font-black text-white dark:text-zinc-900 uppercase tracking-wider">
								- Spraguerrrr
							</p>
						</div>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						animate={isInView ? { opacity: 1, x: 0 } : {}}
						transition={{ duration: 0.6, delay: 0.15 }}
						className="relative h-125 lg:h-150 w-full flex items-center justify-center lg:justify-end mt-8 lg:mt-0"
					>
						<div className="relative w-full h-full">
							<Image
								src={IMAGES.home.jamesSprague}
								alt="James Sprague - CrossFit Games Champion"
								fill
								className="object-contain object-center"
								sizes="(max-width: 768px) 90vw, (max-width: 1200px) 50vw, 600px"
								priority={false}
								quality={60}
							/>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}
